import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Loader2, ShieldAlert, ShieldCheck, History, ListChecks,
  PencilLine, Lock, Ban, CornerDownRight, Trash2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime, statusMeta } from "@/lib/statuses";
import { CUSTOMS_STATUSES } from "@/lib/statuses";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY_EVENT = {
  status: "in_transit", occurred_at: "", location_city: "", location_country: "",
  facility: "", public_note: "", customs_reference: "", customs_authority: "",
  customs_reason: "", required_action: "", internal_notes: "",
};

export default function ShipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [eventOpen, setEventOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const [correctOf, setCorrectOf] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const deleteShipment = async () => {
    setDeleting(true);
    try {
      await api.delete(`/shipments/${id}`);
      toast.success(`Shipment ${s.tracking_number} deleted.`);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/shipments/${id}`);
    setData(data);
  }, [id]);

  useEffect(() => {
    load();
    api.get("/meta/statuses").then((r) => setStatuses(r.data.statuses));
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd = (preset) => {
    setCorrectOf(null);
    setVoidReason("");
    setForm({ ...EMPTY_EVENT, ...(preset || {}) });
    setEventOpen(true);
  };

  const openCorrect = (ev) => {
    setCorrectOf(ev.id);
    setVoidReason("");
    setForm({
      status: ev.status,
      occurred_at: ev.occurred_at ? ev.occurred_at.slice(0, 16) : "",
      location_city: ev.location_city || "", location_country: ev.location_country || "",
      facility: ev.facility || "", public_note: ev.public_note || "",
      customs_reference: ev.customs_reference || "", customs_authority: ev.customs_authority || "",
      customs_reason: ev.customs_reason || "", required_action: ev.required_action || "",
      internal_notes: ev.internal_notes || "",
    });
    setEventOpen(true);
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.occurred_at) payload.occurred_at = new Date(payload.occurred_at).toISOString();
      else delete payload.occurred_at;
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      if (correctOf) {
        if (!voidReason.trim()) {
          toast.error("A correction reason is required.");
          setSaving(false);
          return;
        }
        payload.void_reason = voidReason;
        await api.post(`/shipments/${id}/events/${correctOf}/correct`, payload);
        toast.success("Event corrected — original preserved in history.");
      } else {
        await api.post(`/shipments/${id}/events`, payload);
        toast.success("Tracking event added.");
      }
      setEventOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center p-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      </div>
    );
  }

  const s = data.shipment;
  const isCustoms = CUSTOMS_STATUSES.has(form.status);
  const isHeld = s.status === "held_by_customs";

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => nav("/dashboard")} data-testid="back-to-dashboard" className="mb-4 gap-1.5 text-slate-500">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Button>

        <Card className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-slate-500">{s.tracking_number}</span>
                {s.is_demo && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">Demo</span>}
              </div>
              <h1 className="font-heading mt-1 text-2xl font-bold text-slate-900">{s.description}</h1>
              <p className="mt-1 text-sm text-slate-500">
                {s.origin_city}, {s.origin_country} → {s.dest_city}, {s.dest_country} · {s.service_level}
              </p>
            </div>
            <StatusBadge status={s.status} />
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="add-event-modal-trigger" onClick={() => openAdd()} className="gap-1.5 bg-orange-600 hover:bg-orange-700">
              <Plus className="h-4 w-4" /> Add event
            </Button>
            {!isHeld ? (
              <Button
                variant="outline"
                data-testid="customs-hold-toggle-btn"
                onClick={() => openAdd({ status: "held_by_customs" })}
                className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <ShieldAlert className="h-4 w-4" /> Record customs hold
              </Button>
            ) : (
              <Button
                variant="outline"
                data-testid="customs-release-btn"
                onClick={() => openAdd({ status: "released_by_customs", public_note: "Cleared customs." })}
                className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <ShieldCheck className="h-4 w-4" /> Record customs release
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="delete-shipment-btn"
                  className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-shipment-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this shipment?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes shipment{" "}
                    <span className="font-mono font-medium">{s.tracking_number}</span> along with all its
                    tracking events and audit history. This cannot be undone, and the tracking number will
                    stop working publicly.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="delete-confirm-btn"
                    onClick={deleteShipment}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete permanently"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Card>

        <Card className="mt-5 border-slate-200 bg-slate-50/60 p-4">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <Lock className="h-3.5 w-3.5" /> Private details — staff only
          </p>
          <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <p><span className="text-slate-400">Recipient:</span> <span className="text-slate-700">{s.recipient_name || "—"}</span></p>
            <p><span className="text-slate-400">Phone:</span> <span className="text-slate-700">{s.recipient_phone || "—"}</span></p>
            <p className="sm:col-span-2"><span className="text-slate-400">Address:</span> <span className="text-slate-700">{s.recipient_address || "—"}</span></p>
            <p className="sm:col-span-2"><span className="text-slate-400">Internal notes:</span> <span className="text-slate-700">{s.internal_notes || "—"}</span></p>
          </div>
        </Card>

        <Tabs defaultValue="events" className="mt-6">
          <TabsList>
            <TabsTrigger value="events" data-testid="events-tab" className="gap-1.5"><ListChecks className="h-4 w-4" /> Events</TabsTrigger>
            <TabsTrigger value="audit" data-testid="audit-trail-tab" className="gap-1.5"><History className="h-4 w-4" /> Audit Trail</TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <Card className="divide-y divide-slate-100">
              {data.events.map((ev) => {
                const meta = statusMeta(ev.status);
                const Icon = meta.icon;
                return (
                  <div key={ev.id} data-testid={`event-${ev.id}`} className={`p-4 ${ev.is_void ? "bg-slate-50" : ""}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
                          <Icon className="h-4 w-4" />
                        </span>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`font-medium ${ev.is_void ? "text-slate-400 line-through" : "text-slate-900"}`}>{meta.label}</span>
                            {ev.correction_of && <span className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700"><CornerDownRight className="h-3 w-3" /> Correction</span>}
                            {ev.is_void && <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600"><Ban className="h-3 w-3" /> Superseded</span>}
                          </div>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {[ev.facility, ev.location_city, ev.location_country].filter(Boolean).join(" · ") || "No location"}
                          </p>
                          {ev.public_note && <p className="mt-1 text-sm text-slate-600">{ev.public_note}</p>}
                          {(ev.customs_reason || ev.customs_authority || ev.customs_reference || ev.internal_notes) && (
                            <div className="mt-2 space-y-0.5 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                              {ev.customs_reference && <p><span className="font-semibold">Ref (private):</span> {ev.customs_reference}</p>}
                              {ev.customs_authority && <p><span className="font-semibold">Authority (private):</span> {ev.customs_authority}</p>}
                              {ev.customs_reason && <p><span className="font-semibold">Reason (public):</span> {ev.customs_reason}</p>}
                              {ev.required_action && <p><span className="font-semibold">Next step (public):</span> {ev.required_action}</p>}
                              {ev.internal_notes && <p className="text-slate-600"><span className="font-semibold">Internal note:</span> {ev.internal_notes}</p>}
                            </div>
                          )}
                          {ev.void_reason && <p className="mt-1 text-xs italic text-red-500">Correction reason: {ev.void_reason}</p>}
                          <div className="mt-1.5 flex gap-4 font-mono text-[11px] text-slate-400">
                            <span>occurred {fmtDateTime(ev.occurred_at)}</span>
                            <span>entered {fmtDateTime(ev.recorded_at)}</span>
                          </div>
                        </div>
                      </div>
                      {!ev.is_void && (
                        <Button variant="ghost" size="sm" data-testid={`correct-event-${ev.id}`} onClick={() => openCorrect(ev)} className="gap-1 text-slate-400 hover:text-slate-700">
                          <PencilLine className="h-3.5 w-3.5" /> Correct
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card className="divide-y divide-slate-100" data-testid="audit-list">
              {data.audit.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 p-3.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{a.action.replace(/_/g, " ")}</p>
                    <p className="text-xs text-slate-500">{a.details}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-600">{a.actor_email}</p>
                    <p className="font-mono text-[11px] text-slate-400">{fmtDateTime(a.timestamp)}</p>
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={eventOpen} onOpenChange={setEventOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{correctOf ? "Correct event" : "Add tracking event"}</DialogTitle>
            <DialogDescription>
              {correctOf
                ? "The original event is preserved and marked superseded — never deleted."
                : "Only the public note and confirmed customs fields are visible to customers."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEvent} data-testid="event-form" className="space-y-3">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="event-status-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statuses.map((st) => (
                    <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>When it happened</Label>
                <Input type="datetime-local" data-testid="event-occurred" value={form.occurred_at} onChange={set("occurred_at")} className="mt-1" />
                <p className="mt-1 text-[11px] text-slate-400">Blank = now. Entry time recorded separately.</p>
              </div>
              <div>
                <Label>Facility</Label>
                <Input data-testid="event-facility" value={form.facility} onChange={set("facility")} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>City</Label>
                <Input data-testid="event-city" value={form.location_city} onChange={set("location_city")} className="mt-1" />
              </div>
              <div>
                <Label>Country</Label>
                <Input data-testid="event-country" value={form.location_country} onChange={set("location_country")} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Public note (shown to customer)</Label>
              <Textarea data-testid="event-note" value={form.public_note} onChange={set("public_note")} className="mt-1" rows={2} />
            </div>

            {isCustoms && (
              <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Customs details</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="customs-reference" value={form.customs_reference} onChange={set("customs_reference")} placeholder="Reference # (private)" />
                  <Input data-testid="customs-authority" value={form.customs_authority} onChange={set("customs_authority")} placeholder="Authority / broker (private)" />
                </div>
                <Textarea data-testid="customs-reason" value={form.customs_reason} onChange={set("customs_reason")} placeholder="Confirmed reason (PUBLIC)" rows={2} />
                <Textarea data-testid="customs-action" value={form.required_action} onChange={set("required_action")} placeholder="Required next step (PUBLIC)" rows={2} />
                <Input data-testid="customs-internal" value={form.internal_notes} onChange={set("internal_notes")} placeholder="Internal notes (private)" />
                <p className="text-[11px] text-amber-700">Only the reason and next step are shown publicly. Leave them blank to keep the hold reason private for now.</p>
              </div>
            )}

            {correctOf && (
              <div>
                <Label className="text-red-600">Correction reason (required)</Label>
                <Input data-testid="void-reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} className="mt-1" placeholder="Why is the original being corrected?" />
                <p className="mt-1 text-[11px] text-slate-400">The original event is preserved and marked superseded — never deleted.</p>
              </div>
            )}

            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="event-submit" className="bg-slate-900 hover:bg-slate-800">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : correctOf ? "Save correction" : "Add event"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
